import type { WorkbookDistinctValues, WorkbookTable } from '@erp/shared-types';

/** The most preview rows a sample of Distinct values counts. */
export const SAMPLE_ROWS = 100;

/**
 * A Field's Distinct values as a filter menu shows them: a Result's, or a
 * sample counted from a preview's first rows (`sampleRows` of them), which a
 * Run leaves out when it doesn't read the column (column pruning).
 */
export type ShownDistinctValues = WorkbookDistinctValues & {
  sampleRows?: number;
};

/**
 * Distinct values counted from a preview's first rows, a sample: counts are
 * of those rows only, and it is truncated, since the rest aren't read.
 */
export function sampleDistinctValues(
  table: WorkbookTable,
  name: string,
): ShownDistinctValues | undefined {
  const column = table.columns.indexOf(name);
  const rows = table.rows.slice(0, SAMPLE_ROWS);
  if (column < 0 || !rows.length) return undefined;
  const counts = new Map<string, number>();
  let emptyCount = 0;
  let errorCount = 0;
  for (const row of rows) {
    const cell = row[column] ?? null;
    if (cell === null || cell === '') emptyCount++;
    else if (typeof cell === 'object') errorCount++;
    else counts.set(String(cell), (counts.get(String(cell)) ?? 0) + 1);
  }
  return {
    column: name,
    values: [...counts]
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => (a.value < b.value ? -1 : a.value > b.value ? 1 : 0)),
    emptyCount,
    errorCount,
    truncated: true,
    sampleRows: rows.length,
  };
}

/**
 * A Field's Distinct values: the Result's when it recorded them, else a
 * sample from `preview`'s first rows when it has the column; undefined with
 * neither, for the menu to say to run.
 */
export function fieldDistinctValues(
  recorded: readonly WorkbookDistinctValues[] | undefined,
  name: string,
  preview: WorkbookTable | undefined,
): ShownDistinctValues | undefined {
  return (
    recorded?.find((entry) => entry.column === name) ??
    (preview ? sampleDistinctValues(preview, name) : undefined)
  );
}
